import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Activity, ArrowLeft, ArrowRight, Clock, Handshake, Star, Target } from 'lucide-react';
import StatCard from '../AnalyticsComponents/StatCard';
import ChartCard, { ChartContainer } from '../AnalyticsComponents/ChartCard';
import { fmt, labelStyle, tooltipStyle } from '../AnalyticsComponents/analyticsUtils';
import { extractPlayerSeasonStats, filterPlayerSpecificResults } from '../../helpers/entities';
import { ToolResultBody } from './ToolResult';
import PlayerIntelligence from '../intel/PlayerIntelligence';

const renderValue = value => {
  if (value == null) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const scaleIndex = (value, cap) => (value == null ? 0 : clamp((value / cap) * 100, 0, 100));

const isObject = value => value && typeof value === 'object' && !Array.isArray(value);

const PROFILE_SKIP = new Set(['id', 'uuid', 'image', 'image_path', 'type']);

const NAME_HINTS = [
  'name',
  'player_name',
  'team_name',
  'player',
  'team',
  'title',
  'entity_name',
  'rival_team_name',
  'season_name',
];

const NUMERIC_HINTS = ['total', 'value', 'rating', 'points', 'goals', 'assists', 'count', 'appearances'];

function pickName(item, key) {
  const value = item?.[key];
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (isObject(value) && typeof value.name === 'string' && value.name.trim()) return value.name.trim();
  return '';
}

function chartFromList(data) {
  if (!Array.isArray(data) || data.length < 2 || !data.every(isObject)) return null;

  const sample = data[0];
  const nameKey = NAME_HINTS.find(key => pickName(sample, key));
  const numKey =
    NUMERIC_HINTS.find(key => typeof sample[key] === 'number') ||
    Object.keys(sample).find(key => typeof sample[key] === 'number');

  if (!nameKey || !numKey) return null;

  const rows = data
    .map(item => ({ name: pickName(item, nameKey), value: Number(item[numKey]) || 0 }))
    .filter(row => row.name && row.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 15);

  return rows.length ? rows : null;
}

function extractPlayerId(entity) {
  const raw = entity?.raw;
  return raw?.player_id ?? raw?.id ?? raw?.player?.id ?? null;
}

export default function PlayerDashboard({ entity, results }) {
  const [showFull, setShowFull] = useState(false);
  const playerId = extractPlayerId(entity);
  const seasonStats = useMemo(() => extractPlayerSeasonStats(results, entity), [results, entity]);
  const stats = seasonStats?.stats || {};
  const hasStats = Object.keys(stats).length > 0;

  const goals = stats.goals ?? null;
  const assists = stats.assists ?? null;
  const appearances = stats.appearances ?? null;
  const minutes = stats.minutes_played ?? null;
  const rating = stats.rating ?? null;
  const shotsOnTarget = stats.shots_on_target ?? null;
  const keyPasses = stats.key_passes ?? null;
  const tackles = stats.tackles ?? null;
  const passes = stats.passes ?? null;

  const profileFacts = (entity?.details || []).filter(({ key }) => !PROFILE_SKIP.has(key));

  const barData = [
    goals != null && { name: 'Goals', value: goals },
    assists != null && { name: 'Assists', value: assists },
    shotsOnTarget != null && { name: 'Shots on target', value: shotsOnTarget },
    keyPasses != null && { name: 'Key passes', value: keyPasses },
    tackles != null && { name: 'Tackles', value: tackles },
  ].filter(Boolean);

  const radarData = [
    rating != null && { metric: 'Rating', value: scaleIndex(rating, 10) },
    goals != null && { metric: 'Goals', value: scaleIndex(goals, 30) },
    assists != null && { metric: 'Assists', value: scaleIndex(assists, 20) },
    passes != null && { metric: 'Passes', value: scaleIndex(passes, 2000) },
    keyPasses != null && { metric: 'Key passes', value: scaleIndex(keyPasses, 100) },
    tackles != null && { metric: 'Tackles', value: scaleIndex(tackles, 100) },
  ].filter(Boolean);

  const goalsBreakdown = seasonStats?.goalsBreakdown;
  const donutData =
    goalsBreakdown && goalsBreakdown.total > 0
      ? [
          { name: 'Open play', value: Math.max(0, goalsBreakdown.total - goalsBreakdown.penalties) },
          { name: 'Penalties', value: goalsBreakdown.penalties },
        ]
      : null;

  const kpis = [
    { label: 'Goals', value: goals != null ? fmt(goals) : '—', icon: Target, color: '#10B981' },
    { label: 'Assists', value: assists != null ? fmt(assists) : '—', icon: Handshake, color: '#3B82F6' },
    { label: 'Appearances', value: appearances != null ? fmt(appearances) : '—', icon: Activity, color: '#8B5CF6' },
    { label: 'Minutes', value: minutes != null ? fmt(minutes) : '—', icon: Clock, color: '#9AAAB8' },
    { label: 'Rating', value: rating != null ? Number(rating).toFixed(1) : '—', icon: Star, color: '#9AAAB8' },
  ];

  const playerResults = useMemo(() => filterPlayerSpecificResults(results, entity), [results, entity]);

  if (showFull && playerId) {
    return (
      <div>
        <div className="border-b border-white/10 px-6 py-3">
          <button
            type="button"
            onClick={() => setShowFull(false)}
            className="flex items-center gap-1.5 text-xs font-medium text-mute transition-colors hover:text-ink">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to summary
          </button>
        </div>
        <PlayerIntelligence playerId={playerId} showBack={false} className="entity-dashboard" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-4 border-b border-white/10 p-6 sm:flex-row sm:items-center">
        <div className="flex h-40 w-40 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-white/15 bg-white/10">
          {entity.image ? (
            <img
              src={entity.image}
              alt={entity.name}
              className="h-full w-full object-cover"
              onError={e => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <span className="text-5xl font-bold text-[rgb(154,170,184)]">{entity.name?.charAt(0)?.toUpperCase() || '?'}</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-widest text-mute">Player</p>
          <h2 className="text-3xl font-bold leading-tight text-ink">{entity.name}</h2>
          {profileFacts.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {profileFacts.map(({ key, label, value }) => (
                <div key={key} className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs">
                  <span className="text-mute">{label}: </span>
                  <span className="font-medium text-ink">{renderValue(value)}</span>
                </div>
              ))}
            </div>
          )}
          {playerId && (
            <button
              type="button"
              onClick={() => setShowFull(true)}
              className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold text-ink transition-colors hover:bg-white/10">
              See full player intelligence
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="space-y-6 p-6">
        {!hasStats ? (
          <p className="text-sm text-mute">No season statistics available for this player.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
              {kpis.map(kpi => (
                <StatCard
                  key={kpi.label}
                  label={kpi.label}
                  value={kpi.value}
                  sub={seasonStats?.seasonName || 'This season'}
                  icon={kpi.icon}
                  color={kpi.color}
                />
              ))}
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              {donutData && (
                <ChartCard title="Goal Breakdown" subtitle="Open play vs penalties">
                  <ChartContainer height={240}>
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie
                          data={donutData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={85}
                          paddingAngle={3}>
                          <Cell fill="#9AAAB8" />
                          <Cell fill="#EF4444" />
                        </Pie>
                        <Tooltip contentStyle={tooltipStyle} labelStyle={labelStyle} />
                        <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                </ChartCard>
              )}

              {radarData.length >= 3 && (
                <ChartCard title="Skill Profile" subtitle="Indexed 0–100 across key metrics">
                  <ChartContainer height={240}>
                    <ResponsiveContainer width="100%" height={240}>
                      <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="72%">
                        <PolarGrid stroke="#25252B" />
                        <PolarAngleAxis dataKey="metric" tick={{ fill: '#a0a0a0', fontSize: 10 }} />
                        <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} />
                        <Radar dataKey="value" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.35} strokeWidth={2} />
                        <Tooltip contentStyle={tooltipStyle} labelStyle={labelStyle} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                </ChartCard>
              )}

              {barData.length > 0 && (
                <div className="lg:col-span-2">
                  <ChartCard title="Season Stats" subtitle={seasonStats?.seasonName || 'This season'}>
                    <ChartContainer height={240}>
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={barData} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#222227" vertical={false} />
                          <XAxis dataKey="name" stroke="#707070" fontSize={10} tickLine={false} axisLine={false} interval={0} />
                          <YAxis stroke="#707070" fontSize={11} tickLine={false} axisLine={false} tickFormatter={fmt} />
                          <Tooltip contentStyle={tooltipStyle} labelStyle={labelStyle} cursor={{ fill: '#ffffff08' }} />
                          <Bar dataKey="value" fill="#9AAAB8" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </ChartContainer>
                  </ChartCard>
                </div>
              )}
            </div>
          </>
        )}

        {playerResults.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-mute">Player-specific</h3>
            {playerResults.map((result, index) => {
              const chartRows = chartFromList(result.data);
              return (
                <div key={`${result.tool}-${index}`}>
                  {chartRows ? (
                    <ChartCard title={result.label} subtitle={`${chartRows.length} entries`}>
                      <ChartContainer height={240}>
                        <ResponsiveContainer width="100%" height={240}>
                          <BarChart
                            data={chartRows}
                            layout="vertical"
                            margin={{ top: 5, right: 20, left: 10, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#222227" horizontal={false} />
                            <XAxis type="number" stroke="#707070" fontSize={11} tickLine={false} axisLine={false} />
                            <YAxis
                              type="category"
                              dataKey="name"
                              stroke="#a0a0a0"
                              fontSize={11}
                              tickLine={false}
                              axisLine={false}
                              width={140}
                            />
                            <Tooltip contentStyle={tooltipStyle} labelStyle={labelStyle} cursor={{ fill: '#ffffff08' }} />
                            <Bar dataKey="value" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </ChartContainer>
                    </ChartCard>
                  ) : (
                    <div className="overflow-hidden rounded-xl border border-white/10 bg-white/5">
                      <div className="border-b border-white/10 px-4 py-2 text-sm font-medium text-ink">
                        {result.label}
                      </div>
                      <div className="p-4">
                        <ToolResultBody result={result} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
